import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

/* eslint-disable consistent-return */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { canUseDom, smooth as SmoothScroll } from '@sainsburys-tech/boltui-utils';
import { useResizeEvent } from '@sainsburys-tech/boltui-hooks';
import * as Styled from './Tooltip.styles';

var Tooltip = function Tooltip(_ref) {
  var className = _ref.className,
      children = _ref.children,
      content = _ref.content,
      width = _ref.width,
      durationToShow = _ref.durationToShow,
      position = _ref.position,
      direction = _ref.direction,
      mobileDirection = _ref.mobileDirection,
      enabled = _ref.enabled,
      defaultDisplay = _ref.defaultDisplay,
      displayAfter = _ref.displayAfter,
      disableOutsideTouchHandler = _ref.disableOutsideTouchHandler,
      disableScrollIntoViewport = _ref.disableScrollIntoViewport,
      kind = _ref.kind,
      onClose = _ref.onClose,
      dataTest = _ref['data-test'];

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      visible = _useState2[0],
      setVisible = _useState2[1];

  var _useState3 = useState(function () {
    return canUseDom() ? window.innerWidth : 0;
  }),
      _useState4 = _slicedToArray(_useState3, 2),
      pageWidth = _useState4[0],
      setPageWidth = _useState4[1];

  var contentRef = useRef(null);
  var bubbleRef = useRef(null);
  var isLoadingRef = useRef(true);
  var currentDirection = useMemo(function () {
    return mobileDirection && pageWidth < 576 ? mobileDirection : direction;
  }, [pageWidth, mobileDirection]);
  var contentOffsetWidth = useMemo(function () {
    return contentRef.current ? contentRef.current.offsetWidth : 0;
  }, [contentRef.current]);
  var arrowPositionStyle = useMemo(function () {
    if (['up', 'down'].includes(currentDirection)) {
      var minimumArrowOffset = 10;
      var arrowOffset = position === 'center' ? '50%' : "".concat(Math.max(minimumArrowOffset, contentOffsetWidth / 2), "px");
      var arrowPosition = position === 'center' ? 'left' : position;
      return _defineProperty({}, arrowPosition, arrowOffset);
    }

    return undefined;
  }, [currentDirection, position, contentOffsetWidth]);
  var bubbleIsInViewport = useMemo(function () {
    if (visible && bubbleRef.current) {
      var _bubbleRef$current$ge = bubbleRef.current.getBoundingClientRect(),
          _bubbleRef$current$ge2 = _bubbleRef$current$ge.bottom,
          bottom = _bubbleRef$current$ge2 === void 0 ? 0 : _bubbleRef$current$ge2;

      return bottom <= global.window.innerHeight;
    }

    return false;
  }, [visible, bubbleRef.current]);
  var toggleBubble = useCallback(function (override) {
    if (enabled || visible) {
      var nextVisible = typeof override === 'boolean' ? override : !visible;

      if (nextVisible !== visible) {
        if (!nextVisible && onClose) {
          onClose();
        }

        setVisible(nextVisible);
      }
    }
  }, [enabled, onClose, visible]);
  var handleOutsideClick = useCallback(function (e) {
    if (!bubbleRef.current.contains(e.target) && !contentRef.current.contains(e.target)) {
      toggleBubble(false);
      document.body.removeEventListener('click', handleOutsideClick);
    }
  }, [bubbleRef.current, contentRef.current]);
  var scrollBubbleIntoViewport = useCallback(function () {
    if (bubbleRef.current) {
      var _bubbleRef$current$ge3 = bubbleRef.current.getBoundingClientRect(),
          _bubbleRef$current$ge4 = _bubbleRef$current$ge3.bottom,
          bottom = _bubbleRef$current$ge4 === void 0 ? 0 : _bubbleRef$current$ge4;

      var bubblePosition = window.pageYOffset - (global.window.innerHeight - bottom);
      SmoothScroll.scrollHere(bubblePosition, -10);
    }
  }, [bubbleRef.current]);
  useResizeEvent({
    resizeHandler: function resizeHandler() {
      return setPageWidth(window.innerWidth);
    }
  });
  useEffect(function () {
    if (!disableOutsideTouchHandler && visible) {
      document.body.addEventListener('click', handleOutsideClick);
      return function () {
        return document.body.removeEventListener('click', handleOutsideClick);
      };
    }
  }, [disableOutsideTouchHandler, visible]);
  useEffect(function () {
    if (!disableScrollIntoViewport && visible && !bubbleIsInViewport) {
      scrollBubbleIntoViewport();
    }
  }, [disableScrollIntoViewport, visible, bubbleIsInViewport]);
  useEffect(function () {
    if (isLoadingRef.current && defaultDisplay) {
      if (displayAfter > 0) {
        var displayAfterTimeout = setTimeout(function () {
          return toggleBubble(true);
        }, displayAfter);
        return function () {
          return clearTimeout(displayAfterTimeout);
        };
      }

      toggleBubble(true);
    }

    isLoadingRef.current = false;
  }, [defaultDisplay, displayAfter, isLoadingRef.current]);
  useEffect(function () {
    if (visible && durationToShow > 0) {
      var durationTimeout = setTimeout(function () {
        return toggleBubble(false);
      }, durationToShow);
      return function () {
        return clearTimeout(durationTimeout);
      };
    }
  }, [visible, durationToShow]);
  return /*#__PURE__*/React.createElement(Styled.Wrapper, {
    className: className
  }, /*#__PURE__*/React.createElement("div", {
    role: "button",
    tabIndex: "0",
    "data-test": "".concat(dataTest, "-target"),
    onClick: toggleBubble,
    onKeyDown: function onKeyDown(e) {
      return e.key === 'Enter' && toggleBubble();
    },
    ref: contentRef
  }, children), /*#__PURE__*/React.createElement(Styled.Bubble, {
    visible: visible,
    direction: currentDirection + position,
    ref: bubbleRef,
    kind: kind,
    "data-test": "".concat(dataTest, "-bubble")
  }, /*#__PURE__*/React.createElement(Styled.Arrow, {
    direction: currentDirection,
    style: arrowPositionStyle,
    kind: kind
  }), /*#__PURE__*/React.createElement(Styled.Content, {
    width: width
  }, /*#__PURE__*/React.createElement(Styled.Body, null, content), /*#__PURE__*/React.createElement(Styled.Close, {
    type: "button",
    disabled: !visible,
    onClick: toggleBubble,
    "data-test": "".concat(dataTest, "-close"),
    "aria-label": "Close the tooltip"
  }, /*#__PURE__*/React.createElement(Styled.CloseIcon, {
    width: "20",
    height: "20"
  })))));
};

export default Tooltip;
Tooltip.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  content: PropTypes.element.isRequired,
  width: PropTypes.number.isRequired,
  durationToShow: PropTypes.number,
  position: PropTypes.oneOf(['left', 'right', 'center']),
  direction: PropTypes.oneOf(['down', 'up', 'left', 'right']).isRequired,
  mobileDirection: PropTypes.oneOf(['down', 'up', 'left', 'right']),
  enabled: PropTypes.bool,
  defaultDisplay: PropTypes.bool,
  displayAfter: PropTypes.number,
  disableOutsideTouchHandler: PropTypes.bool,
  disableScrollIntoViewport: PropTypes.bool,
  kind: PropTypes.oneOf(['success', 'info', 'error']),
  'data-test': PropTypes.string,
  onClose: PropTypes.func
};
Tooltip.defaultProps = {
  enabled: true,
  position: 'left',
  direction: 'down',
  durationToShow: 0,
  defaultDisplay: false,
  displayAfter: 0,
  disableOutsideTouchHandler: false,
  disableScrollIntoViewport: false,
  'data-test': 'component-tooltip'
};